import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDailySummary, getTopProducts, getOrders } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import {
  CalendarIcon,
  Receipt,
  Banknote,
  CreditCard,
  Loader2,
  RefreshCw,
  Package,
  Users,
  SlidersHorizontal,
  Share2,
  Printer,
  ArrowRight,
  Store,
  Settings,
  Shield,
  Table2,
} from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';

const SHORTCUTS = [
  {
    title: 'Artikel pflegen',
    description: 'Artikel, Gruppen und Optionen verwalten',
    icon: Package,
    path: '/admin/articles/studio',
  },
  {
    title: 'Tische & Räume',
    description: 'Räume, Tische und Raumplan anpassen',
    icon: Table2,
    path: '/admin/venues/tables',
  },
  {
    title: 'Benutzer',
    description: 'Mitarbeiter, PINs und Rollen verwalten',
    icon: Users,
    path: '/admin/basic/users',
  },
  {
    title: 'Unternehmen',
    description: 'Stammdaten, Fiskal- und Zahlungsarten pflegen',
    icon: Store,
    path: '/admin/basic/company',
  },
  {
    title: 'Rollen',
    description: 'Berechtigungen für Admin, Service und Manager',
    icon: Shield,
    path: '/admin/basic/user-roles',
  },
  {
    title: 'System',
    description: 'Drucker, Fiskaltrust und Kassenlogik konfigurieren',
    icon: Settings,
    path: '/admin/basic/fiscal',
  },
];

function formatMoney(value) {
  return new Intl.NumberFormat('de-AT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(value || 0);
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [summary, setSummary] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadDashboardData = useCallback(async () => {
    setIsLoading(true);
    const dateStr = format(selectedDate, 'yyyyMMdd');

    try {
      const [summaryData, products, orders] = await Promise.all([
        getDailySummary(dateStr),
        getTopProducts(dateStr, 5),
        getOrders('completed', dateStr),
      ]);

      setSummary(summaryData);
      setTopProducts(products);
      setRecentOrders(orders.slice(0, 10));
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Dashboard konnte nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const totalSales = summary?.total_sales || 0;
  const netSales = summary?.net_sales || 0;
  const taxes = Math.max(0, totalSales - netSales);
  const totalOrders = summary?.total_orders || 0;
  const avgOrder = totalOrders > 0 ? totalSales / totalOrders : 0;
  const cashTotal = summary?.cash_total || 0;
  const cardTotal = summary?.card_total || 0;
  const totalPayments = cashTotal + cardTotal || 1;
  const cashShare = (cashTotal / totalPayments) * 100;
  const cardShare = (cardTotal / totalPayments) * 100;

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#132d39]" />
      </div>
    );
  }

  return (
    <div className="space-y-8 px-8 py-8" data-testid="admin-dashboard">
      <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Manager Portal</p>
          <h1 className="mt-2 text-5xl font-bold tracking-tight text-[#132d39]">Dashboard</h1>
          <p className="mt-3 max-w-2xl text-base text-slate-500">
            Tagesgeschäft, Umsatzentwicklung und die wichtigsten Verwaltungswege für dein Kassasystem an einem Ort.
          </p>
        </div>

        <div className="grid min-w-[320px] gap-4 sm:grid-cols-2 xl:w-[440px]">
          <Card className="rounded-[28px] border-slate-200 bg-white shadow-sm">
            <CardContent className="p-5">
              <p className="text-sm font-medium text-slate-500">Bestellungen</p>
              <p className="mt-3 text-4xl font-bold tracking-tight text-[#132d39]">{totalOrders}</p>
              <p className="mt-2 text-sm text-slate-500">Abrechnung für {format(selectedDate, 'dd. MMMM yyyy', { locale: de })}</p>
            </CardContent>
          </Card>

          <Card className="rounded-[28px] border-slate-200 bg-white shadow-sm">
            <CardContent className="p-5">
              <p className="text-sm font-medium text-slate-500">Ø Bonwert</p>
              <p className="mt-3 text-4xl font-bold tracking-tight text-[#132d39]">{formatMoney(avgOrder)}</p>
              <p className="mt-2 text-sm text-slate-500">Hilft bei Preissteuerung und Serviceleistung</p>
            </CardContent>
          </Card>
        </div>
      </div>

      <Card className="rounded-[32px] border-slate-200 bg-white shadow-sm">
        <CardContent className="flex flex-col gap-4 p-4 lg:flex-row lg:items-center lg:justify-between lg:p-6">
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="outline" className="h-14 rounded-2xl border-slate-200 px-5 text-[#132d39]">
              <SlidersHorizontal className="mr-2 h-4 w-4" />
              Filter anpassen
            </Button>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-14 min-w-[220px] justify-start rounded-2xl border-slate-200 px-5 text-left text-[#132d39]">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {format(selectedDate, 'dd.MM.yyyy')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  locale={de}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-500">
              <span className="font-semibold text-[#132d39]">{format(selectedDate, 'dd.MM.yyyy')}</span>
              {' '}Tagesansicht
            </div>
            <Button variant="outline" onClick={loadDashboardData} className="h-14 rounded-2xl border-slate-200 px-5 text-[#132d39]">
              <RefreshCw className="mr-2 h-4 w-4" />
              Neu laden
            </Button>
            <Button className="h-14 rounded-2xl bg-[#71c545] px-6 text-white hover:bg-[#63b13b]">
              <Printer className="mr-2 h-4 w-4" />
              Drucken
            </Button>
            <Button variant="outline" className="h-14 rounded-2xl border-slate-200 px-5 text-[#71c545]">
              <Share2 className="mr-2 h-4 w-4" />
              Teilen
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 xl:grid-cols-[1.8fr_1fr]">
        <Card className="rounded-[32px] border-slate-200 bg-white shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-[2rem] font-bold tracking-tight text-[#314b59]">Sales overview</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="rounded-[28px] bg-slate-50 p-6">
                <p className="text-sm font-medium text-slate-500">Net</p>
                <p className="mt-5 text-5xl font-bold tracking-tight text-[#2b4251]">{formatMoney(netSales)}</p>
              </div>
              <div className="rounded-[28px] bg-slate-50 p-6">
                <p className="text-sm font-medium text-slate-500">Taxes</p>
                <p className="mt-5 text-5xl font-bold tracking-tight text-[#2b4251]">{formatMoney(taxes)}</p>
              </div>
              <div className="rounded-[28px] bg-slate-50 p-6">
                <p className="text-sm font-medium text-slate-500">Gross</p>
                <p className="mt-5 text-5xl font-bold tracking-tight text-[#2b4251]">{formatMoney(totalSales)}</p>
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-3">
              <div className="rounded-[28px] border border-slate-200 p-5">
                <div className="flex items-center gap-2 text-slate-500">
                  <Receipt className="h-4 w-4 text-[#71c545]" />
                  <span className="text-sm font-medium">Bestellungen</span>
                </div>
                <p className="mt-4 text-4xl font-bold text-[#132d39]">{totalOrders}</p>
                <p className="mt-2 text-sm text-slate-500">Abgeschlossene Bons für den gewählten Zeitraum</p>
              </div>

              <div className="rounded-[28px] border border-slate-200 p-5">
                <div className="flex items-center gap-2 text-slate-500">
                  <CreditCard className="h-4 w-4 text-[#5f8dd3]" />
                  <span className="text-sm font-medium">Kartenzahlung</span>
                </div>
                <p className="mt-4 text-4xl font-bold text-[#132d39]">{formatMoney(cardTotal)}</p>
                <p className="mt-2 text-sm text-slate-500">{cardShare.toFixed(0)}% des Zahlungsvolumens</p>
              </div>

              <div className="rounded-[28px] border border-slate-200 p-5">
                <div className="flex items-center gap-2 text-slate-500">
                  <Banknote className="h-4 w-4 text-[#f19a39]" />
                  <span className="text-sm font-medium">Barzahlung</span>
                </div>
                <p className="mt-4 text-4xl font-bold text-[#132d39]">{formatMoney(cashTotal)}</p>
                <p className="mt-2 text-sm text-slate-500">{cashShare.toFixed(0)}% des Zahlungsvolumens</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-[32px] border-slate-200 bg-white shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-[2rem] font-bold tracking-tight text-[#314b59]">Manager shortcuts</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
            {SHORTCUTS.map((shortcut) => {
              const Icon = shortcut.icon;
              return (
                <button
                  key={shortcut.title}
                  onClick={() => navigate(shortcut.path)}
                  className="group rounded-[24px] border border-slate-200 bg-slate-50 p-4 text-left transition-all hover:border-[#cfe3d6] hover:bg-[#eef7f0]"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-[#132d39] shadow-sm">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-[#132d39]">{shortcut.title}</p>
                      <p className="mt-1 text-sm text-slate-500">{shortcut.description}</p>
                    </div>
                    <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-slate-300 transition-transform group-hover:translate-x-1 group-hover:text-[#132d39]" />
                  </div>
                </button>
              );
            })}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.25fr_1fr]">
        <Card className="rounded-[32px] border-slate-200 bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-[2rem] font-bold tracking-tight text-[#314b59]">Top Artikel</CardTitle>
          </CardHeader>
          <CardContent>
            {topProducts.length === 0 ? (
              <div className="rounded-[24px] border border-dashed border-slate-200 px-6 py-12 text-center text-slate-400">
                Keine Verkäufe für den ausgewählten Tag.
              </div>
            ) : (
              <div className="overflow-hidden rounded-[24px] border border-slate-200">
                <div className="grid grid-cols-[56px_1.6fr_0.6fr_0.8fr] gap-4 border-b border-slate-200 bg-slate-50 px-5 py-4 text-sm font-medium text-slate-500">
                  <span>#</span>
                  <span>Artikel</span>
                  <span className="text-right">Menge</span>
                  <span className="text-right">Umsatz</span>
                </div>
                {topProducts.map((product, index) => (
                  <div
                    key={product.product_id}
                    className="grid grid-cols-[56px_1.6fr_0.6fr_0.8fr] gap-4 border-b border-slate-100 px-5 py-4 last:border-0"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-sm font-semibold text-[#132d39]">
                      {index + 1}
                    </span>
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-[#132d39]">{product.product_name}</p>
                      <p className="mt-1 text-sm text-slate-500">Top-Seller im Tagesgeschäft</p>
                    </div>
                    <span className="text-right font-medium text-slate-600">{product.quantity}x</span>
                    <span className="text-right font-semibold text-[#132d39] tabular-nums">{formatMoney(product.total)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-[32px] border-slate-200 bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-[2rem] font-bold tracking-tight text-[#314b59]">Letzte Bestellungen</CardTitle>
          </CardHeader>
          <CardContent>
            {recentOrders.length === 0 ? (
              <div className="rounded-[24px] border border-dashed border-slate-200 px-6 py-12 text-center text-slate-400">
                Noch keine abgeschlossenen Bons.
              </div>
            ) : (
              <div className="space-y-3">
                {recentOrders.map((order) => (
                  <div key={order.id} className="rounded-[24px] border border-slate-200 p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-mono text-sm font-semibold text-[#132d39]">{order.order_number}</p>
                        <p className="mt-1 text-sm text-slate-500">{order.table_name || 'Ohne Tisch'}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-[#132d39] tabular-nums">{formatMoney(order.total)}</p>
                        <p className="mt-1 text-sm text-slate-500">
                          {order.payment_method === 'cash' ? 'Bar' : 'Karte'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
        <Card className="rounded-[32px] border-slate-200 bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-[2rem] font-bold tracking-tight text-[#314b59]">Zahlungs- und Steuerstruktur</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <div className="rounded-[24px] bg-slate-50 p-5">
              <p className="text-sm font-medium text-slate-500">Bar vs. Karte</p>
              <div className="mt-4 h-3 overflow-hidden rounded-full bg-white">
                <div className="flex h-full">
                  <div className="bg-[#f19a39]" style={{ width: `${cashShare}%` }} />
                  <div className="bg-[#5f8dd3]" style={{ width: `${cardShare}%` }} />
                </div>
              </div>
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Bar</span>
                  <span className="font-semibold text-[#132d39]">{formatMoney(cashTotal)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Karte</span>
                  <span className="font-semibold text-[#132d39]">{formatMoney(cardTotal)}</span>
                </div>
              </div>
            </div>

            <div className="rounded-[24px] bg-slate-50 p-5">
              <p className="text-sm font-medium text-slate-500">MwSt-Aufschlüsselung</p>
              <div className="mt-4 space-y-3">
                {Object.entries(summary?.vat_breakdown || {}).length === 0 && (
                  <p className="text-sm text-slate-400">Keine Steuerdaten verfügbar.</p>
                )}
                {Object.entries(summary?.vat_breakdown || {}).map(([rate, amount]) => (
                  <div key={rate} className="flex items-center justify-between rounded-2xl bg-white px-4 py-3">
                    <span className="font-medium text-slate-500">MwSt {rate}%</span>
                    <span className="font-semibold text-[#132d39] tabular-nums">{formatMoney(amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-[32px] border-slate-200 bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-[2rem] font-bold tracking-tight text-[#314b59]">Heute im Fokus</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-[24px] border border-slate-200 p-5">
              <p className="text-sm font-medium text-slate-500">Rabatte</p>
              <p className="mt-3 text-4xl font-bold text-[#132d39]">{formatMoney(summary?.discount_total || 0)}</p>
              <p className="mt-2 text-sm text-slate-500">Direkt aus dem Tagesgeschäft sichtbar</p>
            </div>

            <div className="rounded-[24px] border border-slate-200 p-5">
              <p className="text-sm font-medium text-slate-500">Servicefrequenz</p>
              <p className="mt-3 text-4xl font-bold text-[#132d39]">{recentOrders.length}</p>
              <p className="mt-2 text-sm text-slate-500">Letzte abgeschlossene Bons im Schnellzugriff</p>
            </div>

            <Button
              onClick={() => navigate('/admin/reports')}
              className="h-14 w-full rounded-2xl bg-[#132d39] text-white hover:bg-[#1d3c4a]"
            >
              Zu den Berichten
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
